import React, { useState, useEffect } from 'react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';

// Note: useDebug is not defined here, so logEvent calls will cause an error.
// You'll need to either pass logEvent as a prop or remove these calls if debugging isn't critical for this isolated component.
// For now, I will comment out the logEvent calls.

// interface DebugInfo { /* ... if needed ... */ }
// const useDebug = () => { /* ... if needed ... */ return { logEvent: console.log, DebugPanel: () => null }; };


export interface CustomDateInputProps {
  id: string;
  value: string;
  onChange: (value: string) => void;
  label: string;
  required?: boolean;
}

const CustomDateInput: React.FC<CustomDateInputProps> = ({
  id,
  value,
  onChange,
  label,
  required = false
}) => {
  // const { logEvent } = useDebug(); // Assuming useDebug is accessible or passed
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    const style = document.createElement('style');
    style.innerHTML = `
      .react-datepicker {
        border: none !important;
        box-shadow: 0 10px 25px rgba(0, 0, 0, 0.15) !important;
        border-radius: 12px !important;
        font-family: inherit !important;
        padding: 16px !important;
        border: 2px solid #fbbf24 !important;
      }
      .react-datepicker__header {
        background-color: #fffbeb !important;
        border-bottom: 2px solid #fbbf24 !important;
        padding: 16px 12px 12px 12px !important;
        border-radius: 8px 8px 0 0 !important;
      }
      .react-datepicker__current-month {
        font-size: 1rem !important;
        font-weight: 600 !important;
        color: #374151 !important;
        margin-bottom: 8px !important;
      }
      .react-datepicker__day-names {
        margin-top: 8px !important;
      }
      .react-datepicker__day-name {
        color: #6b7280 !important;
        font-size: 0.8rem !important;
        font-weight: 600 !important;
        width: 2.5rem !important;
        line-height: 2.5rem !important;
      }
      .react-datepicker__day {
        color: #374151 !important;
        font-size: 0.9rem !important;
        width: 2.5rem !important;
        line-height: 2.5rem !important;
        margin: 0.15rem !important;
        border-radius: 8px !important;
        transition: all 0.2s ease !important;
        font-weight: 500 !important;
      }
      .react-datepicker__day:hover {
        background-color: #fef3c7 !important;
        color: #92400e !important;
        transform: scale(1.05) !important;
      }
      .react-datepicker__day--today {
        font-weight: 700 !important;
        color: #f59e0b !important;
        background-color: #fef3c7 !important;
        border: 2px solid #f59e0b !important;
      }
      .react-datepicker__day--selected,
      .react-datepicker__day--keyboard-selected {
        background-color: #f59e0b !important;
        color: white !important;
        font-weight: 600 !important;
        box-shadow: 0 4px 8px rgba(245, 158, 11, 0.3) !important;
      }
      .react-datepicker__day--outside-month {
        color: #d1d5db !important;
      }
      .react-datepicker__navigation {
        top: 16px !important;
        width: 2.5rem !important;
        height: 2.5rem !important;
        border-radius: 8px !important;
        transition: all 0.2s ease !important;
        background-color: white !important;
        border: 2px solid #e5e7eb !important;
      }
      .react-datepicker__navigation:hover {
        background-color: #fef3c7 !important;
        border-color: #fbbf24 !important;
        transform: scale(1.05) !important;
      }
      .react-datepicker__navigation--previous {
        left: 12px !important;
      }
      .react-datepicker__navigation--next {
        right: 12px !important;
      }
      .react-datepicker__triangle {
        display: none !important;
      }
    `;
    document.head.appendChild(style);

    return () => {
      document.head.removeChild(style);
    };
  }, []);

  const handleChange = (date: Date | null) => {
    if (date) {
      const formattedDate = date.toISOString().split('T')[0];
      onChange(formattedDate);
      // logEvent('dateInputChange', { value: formattedDate, id }, {});
    }
  };

  return (
    <div className="mb-4">
      <label htmlFor={id} className="text-sm font-semibold text-gray-700 mb-2 flex items-center">
        <div className="w-2 h-2 bg-yellow-400 rounded-full mr-2"></div>
        {label} {required && <span className="text-red-500 ml-1">*</span>}
      </label>
      <div className="relative">
        <DatePicker
          id={id}
          selected={value ? new Date(value) : null}
          onChange={handleChange}
          dateFormat="yyyy-MM-dd"
          className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-400 focus:border-yellow-400 shadow-sm transition-all duration-200 hover:border-gray-300 text-base bg-white"
          placeholderText="Select date"
          showPopperArrow={false}
          popperPlacement="bottom-start"
          calendarClassName="border-0 shadow-none mt-1"
          onCalendarOpen={() => {
            setIsOpen(true);
            // logEvent('datePickerOpen', { id }, {});
          }}
          onCalendarClose={() => setIsOpen(false)}
          customInput={
            <input
              type="text"
              inputMode="none"
              className="w-full px-3 sm:px-4 py-3 border-2 border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-400 focus:border-yellow-400 shadow-sm transition-all duration-200 hover:border-gray-300 text-sm sm:text-base bg-white pr-10 sm:pr-12"
              value={value}
              readOnly
              placeholder="Select date"
              onClick={() => setIsOpen(!isOpen)}
              onFocus={(e) => e.target.blur()}
            />
          }
        />
         <div className="absolute inset-y-0 right-0 pr-2 sm:pr-4 flex items-center pointer-events-none">
            <svg
                className="h-4 w-4 sm:h-5 sm:w-5 text-yellow-500"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
            >
                <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                />
            </svg>
        </div>
      </div>
    </div>
  );
};

export default CustomDateInput; 